import { FormatHelpers } from "../helpers/format-helpers";
import { ObjectHelpers } from "../helpers/object-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { TradeOrderDataModel } from "../models/draw-trade-order/trade-order-data.model";
import { IComponent } from "./component.interface";
import { DrawTradeOrdersComponent } from "./draw-trade-orders.component";
import { InformationPopupComponent } from "./information-popup.component";
import { BracketTradePlotLineComponent } from "./bracket-orders/bracket-trade-plot-line.component";
import { OrderBracketManagerComponent } from "./order-bracket-manager.component";

export class TradePlotLineComponent implements IComponent {
    public id = '';

    private mouseHoverTimer: any;
    public mouseHover = false;
    public moveMode = false;
    private moveRequestPending = false;
    public selected = false;

    private base: IChartContainer;
    public model: TradeOrderDataModel;
    private drawTradeOrdersComponent: DrawTradeOrdersComponent;
    public bracketTradePlotline: BracketTradePlotLineComponent | null = null;
    private cloneId = '';
    public disabled = false;
    private deleted = false;
    private hoverOnRemove = false;
    public isPending = false;
    public pendingOriginalPrice = 0;
    private pendingRemoveTimer: any = null;

    public debouncePendingRemove(delay = 1000) {
        clearTimeout(this.pendingRemoveTimer);
        this.pendingRemoveTimer = setTimeout(() => {
            this.isPending = false;
            this.remove();
        }, delay);
    }

    private g: any = null;
    public hide = false;
    private fObj: any = null;
    public left = -1;
    private top = -1;
    public elementWidth = -1;
    private elementHeight = -1;
    private color = '';
    public divElement: HTMLElement | null = null;
    private lastPriceDivElement: HTMLElement | null = null;
    private orderBracketManager: OrderBracketManagerComponent | null;

    public readonly EXTRA_RIGHT = 85;
    public readonly EXTRA_PADDING_RIGHT = -20;
    public readonly EXTRA_WIDTH_FOR_PROFIT_STOP_ICONS = 54;

    private mouseUpHandler: EventListener;

    constructor(base: IChartContainer, tradeOrder: TradeOrderDataModel, drawTradeOrdersComponent: DrawTradeOrdersComponent) {
        this.base = base;
        this.model = tradeOrder;
        this.drawTradeOrdersComponent = drawTradeOrdersComponent;
        this.id = this.model.getClassName();
        this.cloneId = this.id + '-clone';
        this.color = this.model.tradeType?.toLowerCase() === 'limit'
            ? 'rgb(0, 79, 208)'
            : (this.model.isLong ? 'rgb(38, 142, 55)' : 'rgb(240, 54, 68)');
        this.mouseUpHandler = this.mouseUp.bind(this);

        this.base.tiChartSeries?.subscribeExtremeChange(this.id, () => {
            if (this.deleted || this.disabled || this.moveMode || this.moveRequestPending || this.mouseHover) return;
            this.setIconPosition();
            this.createLastPrice();
        });
        this.base.tiChartSeries?.subscribeRedrawChange(this.id, () => {
            if (this.deleted || this.disabled || this.moveMode || this.moveRequestPending || this.mouseHover) return;
            this.setIconPosition();
            this.createLastPrice();
        });

        this.pendingOriginalPrice = this.model.price;
        this.orderBracketManager = this.base.getComponentsByType(OrderBracketManagerComponent)?.[0] as OrderBracketManagerComponent;

        this.setElementWidthAndHeight();
        this.init();
        this.onEvents();
        
        if (tradeOrder.tradeType?.toLowerCase() === 'limit' || tradeOrder.tradeType?.toLowerCase() === 'stop' || tradeOrder.tradeType?.toLowerCase() === 'stoplimit') {
            this.bracketTradePlotline = new BracketTradePlotLineComponent(this.base, this);
            this.bracketTradePlotline.updateAllTradeOrder(this.drawTradeOrdersComponent.allTradeOrders);
        }

        if (this.orderBracketManager.selectedGroupId === this.model?.id || this.orderBracketManager.ordersAndPositionBracketCount == 0) {
            this.enable();
        } else {
            this.disable();
        }
    }

    public disable() {
        this.disabled = true;
        this.divElement?.setAttribute('style', 'opacity: 0.3 !important; cursor: default !important;');
        this.bracketTradePlotline?.setBracketEnabled(false);
    }

    public enable() {
        this.disabled = false;
        this.divElement?.setAttribute('style', '');
        this.bracketTradePlotline?.setBracketEnabled(true);
    }

    public cancelExecuteTradeImmediately() {
        if (this.bracketTradePlotline) {
            if (this.bracketTradePlotline.requestingProfitTarget) {
                this.bracketTradePlotline.requestingProfitTarget = false;
                this.bracketTradePlotline.removeProfitTarget();
            }

            if (this.bracketTradePlotline.requestingStopLoss) {
                this.bracketTradePlotline.requestingStopLoss = false;
                this.bracketTradePlotline.removeStopLosses();
            }
        }

        if (this.orderBracketManager?.ordersAndPositionBracketCount === 0) {
            this.orderBracketManager.selectedGroupId = null;
            this.orderBracketManager.revalidateSelection(new Set());
        }
    }

    public isMoving(): boolean {
        return this.moveMode || !!this.selected || this.hoverOnRemove;
    }

    public remove() {
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return;
        }

        this.deleted = true;

        this.divElement?.remove();
        this.divElement = null;
        this.lastPriceDivElement?.remove();
        this.lastPriceDivElement = null;
        yAxis.removePlotLine(this.id);
        yAxis.removePlotLine(this.cloneId);
        this.removeStyle();

        try {
            if (this.g) {
                this.g?.destroy();
            }

            this.g = null;
            this.fObj = null;
        } catch { }

        this.bracketTradePlotline?.remove();
        this.base.tiChartSeries.removeSubscriberByKey(this.id);
        this.base.domEvents.removeSubscribersByKey(this.id);
        this.base.tiChartSeries.element.removeEventListener('mouseup', this.mouseUpHandler);
    }

    public update(tradeOrder: TradeOrderDataModel) {
        const previousTradeData: TradeOrderDataModel = ObjectHelpers.copy(this.model);
        this.model = tradeOrder;
        if (this.model.price != previousTradeData.price || this.model.allowMoving != previousTradeData.allowMoving) {
            this.reCreatePlotline();
        }
    }

    public updateAllTradeOrders(allTradeOrders: Array<TradeOrderDataModel>) {
        this.bracketTradePlotline?.updateAllTradeOrder(allTradeOrders);
    }

    public onExtreamChange() {
        if (this.deleted) return;
        if (!this.divElement) {
            this.reCreatePlotline();
        } else if (!this.mouseHover) {
            this.setIconPosition();
            this.createLastPrice();
        }
        this.bracketTradePlotline?.onExtreamChange();
    }

    public onRedraw() {
        if (this.deleted) return;
        if (!this.divElement) {
            this.reCreatePlotline();
        } else if (!this.mouseHover) {
            this.setIconPosition();
            this.createLastPrice();
        }
        this.bracketTradePlotline?.onRedraw();
    }

    private removeStyle() {
        const highChartStyle = this.base.tiChartSeries?.element?.findFirst('.style-' + this.id);
        if (highChartStyle) {
            highChartStyle.remove();
        }
    }

    private createSytle() {
        this.removeStyle();
        const top = '';
        const className = this.id;
        let styleTag = `<style>`;
        if (this.model.allowMoving) {
            styleTag += `.highcharts-plot-line.${className} {stroke-width: 20px !important; stroke: transparent; cursor: move;}`;
            styleTag += `.highcharts-plot-line.${className}-clone {stroke: transparent;}`;
            styleTag += `.highcharts-plot-line-label.${className} {color: ${this.color}; ${top}}`;
        } else {
            styleTag += `.highcharts-plot-line.${className} {stroke-width: 20px !important; stroke: transparent; cursor: default;}`;
            styleTag += `.highcharts-plot-line.${className}-clone {stroke: transparent;}`;
            styleTag += `.highcharts-plot-line-label.${className} {color: ${this.color}; opacity: 0.5; ${top}}`;
        }

        styleTag += `
            .last-price-wrapper.${this.id} {
                background-color: ${this.color} !important;
            } 
            .last-price-wrapper.${this.id} .line {
                position: absolute;
                right: 0px;
                top: 50%;
                background-color: ${this.color} !important;
                height: 1px;
            }
            .last-price-wrapper.${this.id} .last-price p {
                z-index: 1;
                position: relative;
            }
            .last-price-wrapper.${this.id} .last-price:before {
                border-color: transparent ${this.color} transparent transparent !important;
            }
        `;

        styleTag += `</style>`;

        const chartStyleContainer = `<div class="style-${className}">${styleTag}</div>`;
        this.base.tiChartSeries?.element.insertAdjacentHTML('afterbegin', chartStyleContainer);
    }

    private createLastPrice() {
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) return;

        const currentYValue = yAxis.toPixels(this.model.price) ?? 0;
        const myHeight = 7;

        const valueTop = (yAxis.toPixels(yAxis.max) ?? 0) - 16;
        const valueBottom = (yAxis.toPixels(yAxis.min) ?? 0) - 16;

        let top = currentYValue - myHeight;
        let hide = false;

        if (this.model.price > yAxis.max || valueTop > top) {
            top = valueTop;
            hide = true;
        } else if (this.model.price < yAxis.min || top > valueBottom) {
            top = valueBottom;
            hide = true;
        }

        const priceToString = '$' + FormatHelpers.numberToStringThousandSeparators(this.model.price, true);

        if (!this.lastPriceDivElement) {
            this.lastPriceDivElement = this.base.chartWrapper.querySelector<HTMLElement>('.last-price-wrapper.' + this.id) ?? null;
        }

        if (this.lastPriceDivElement) {
            this.lastPriceDivElement.style.top = top + 'px';

            const pTag = this.lastPriceDivElement.querySelector('p');
            if (pTag) {
                pTag.innerHTML = priceToString;
            }
        } else {
            this.lastPriceDivElement = document.createElement('div');
            this.lastPriceDivElement.className = 'last-price-wrapper ' + this.id;
            this.lastPriceDivElement.style.top = top + 'px';
            this.lastPriceDivElement.innerHTML = `
                    <div class="last-price">
                        <p>${priceToString}</p>
                        <div class="line"></div>
                    </div>
                `;

            const highchartContainer = this.base.tiChartSeries.element.findFirst('.highcharts-container');
            if (highchartContainer) {
                highchartContainer.insertAdjacentElement('beforeend', this.lastPriceDivElement);
            }
        }

        if (hide) {
            this.lastPriceDivElement?.classList.add('hidden');
        } else {
            this.lastPriceDivElement?.classList.remove('hidden');
        }
    }

    private init() {
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        this.createSytle();
        if (yAxis) {
            yAxis.addPlotLine({
                value: this.model.price,
                className: this.id,
                id: this.id,
                zIndex: 15,
                events: {
                    mousedown: () => {
                        if (!this.model.allowMoving) {
                            return;
                        }

                        this.base.tiChartSeries.preventDragging = true;
                        this.moveMode = true;
                    }
                }
            });

            yAxis.addPlotLine({
                value: this.model.price,
                className: this.cloneId,
                id: this.cloneId,
                zIndex: 14
            });

            this.createIcon();
            this.createLastPrice();
            setTimeout(() => {
                this.setIconPosition();
                this.createLastPrice();
            }, 500);
        }
    }

    private reCreatePlotline(dragging?: boolean) {
        const yAxis: any = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return;
        }

        this.createSytle();

        yAxis.removePlotLine(this.id);
        yAxis.addPlotLine({
            value: this.model.price,
            className: this.id + '  selected',
            id: this.id,
            zIndex: 15,
            events: {
                mousedown: () => {
                    if (!this.model.allowMoving) {
                        return;
                    }

                    this.base.tiChartSeries.preventDragging = true;
                    this.moveMode = true;
                }
            }
        });

        yAxis.removePlotLine(this.cloneId);
        yAxis.addPlotLine({
            value: this.model.price,
            className: this.cloneId,
            id: this.cloneId,
            zIndex: 14
        });

        this.setElementWidthAndHeight();
        this.createIcon(dragging);
        this.createLastPrice();
    }

    private mouseUp() {
        if (!this.moveMode) {
            return;
        }

        this.moveMode = false;

        if (this.divElement) {
            this.divElement.style.cursor = '';
        }

        if (this.disabled || !this.model.allowMoving) {
            return;
        }

        // Hold moveRequestPending for the broker-request window so redraw callbacks don't snap the line back.
        this.moveRequestPending = true;
        setTimeout(() => {
            this.moveRequestPending = false;
            if (this.model.price === this.pendingOriginalPrice) {
                this.base.tiChartSeries.preventDragging = false;
                return;
            }
            this.isPending = true;
            this.mouseHover = false;
            this.disable();
            this.drawTradeOrdersComponent.registerPendingMove(this.model, this.pendingOriginalPrice);
            this.base.chartService.requestUpdateChartTradeV3(this.model.id, this.model.price);
            this.base.tiChartSeries.preventDragging = false;
        }, 500);
    }

    private onEvents() {
        this.base.domEvents.onMouseMove(this.id, (event: any) => {
            if (this.deleted || !this.model.allowMoving || !this.moveMode || this.disabled || !this.base.tiChartSeries) {
                return;
            }

            const yAxis: any = this.base.tiChartSeries.highStockChart.yAxis[0];
            if (!yAxis) {
                return;
            }

            if (this.divElement) {
                this.divElement.style.cursor = '-webkit-grabbing';
            }

            const pointer = this.base.tiChartSeries.highStockChart.pointer.normalize(event);
            const parentElementRect = this.base.tiChartSeries.chartElement.getBoundingClientRect();
            const yTop = pointer.y - parentElementRect.y;

            const yValue = yAxis.toValue(yTop);
            this.model.price = yValue;

            if (this.model.stopPrice > 0) {
                this.model.stopPrice = yValue;
            } else if (this.model.limitPrice > 0) {
                this.model.limitPrice = yValue;
            }

            this.reCreatePlotline(true);
        });

        this.base.tiChartSeries.element.addEventListener('mouseup', this.mouseUpHandler);
    }

    public toFront() {
        if (!this.model.allowMoving) return;
        if (!!this.g?.element) {
            this.g?.attr({ zIndex: 18 });
        }
    }

    public toNormal() {
        if (!this.model.allowMoving) return;
        if (!!this.g?.element) {
            this.g?.attr({ zIndex: 17 });
        }
    }

    private createIcon(dragging?: boolean) {
        const container = this.base.chartWrapper;
        if (!container) return;

        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) return;

        try {
            if (this.g) {
                this.g?.destroy();
            }
        } catch { }

        this.setInitialTop();
        this.setLeft();
        
        if (dragging) {
            this.base.tiChartSeries.preventDragging = true;
            this.moveMode = true;

            if (this.divElement)
                this.divElement.style.cursor = '-webkit-grabbing';
        }

        if (isNaN(this.top) || isNaN(this.left)) return;

        const renderer = this.base.tiChartSeries.highStockChart.renderer;
        this.hide = false;

        this.divElement = this.getWrapperElement();
        this.g = renderer.g(this.id).attr({
            zIndex: 17
        });

        const requireSelectedGroup = this.orderBracketManager.ordersAndPositionBracketCount > 0;
        if (this.disabled || (requireSelectedGroup && this.orderBracketManager.selectedGroupId != this.model?.id)) {
            this.disable();
        }

        if (this.model.price > yAxis.max || this.model.price < yAxis.min) {
            this.hide = true;
        }

        const foreignObjectElement = renderer.createElement('foreignObject');
        foreignObjectElement.element.appendChild(this.divElement);
        foreignObjectElement.element.setAttribute('width', this.elementWidth);
        foreignObjectElement.element.setAttribute('height', 25 + '');
        foreignObjectElement.element.setAttribute('overflow', 'visible');
        foreignObjectElement.element.setAttribute('x', this.left);
        foreignObjectElement.element.setAttribute('y', this.top);
        foreignObjectElement.add(this.g);

        this.fObj = foreignObjectElement;
        this.g.add();

        let hoverOnRemoveTimer: any = null;

        if (this.model.allowMoving) {
            this.divElement?.addEventListener('mousedown', () => {
                this.base.tiChartSeries.preventDragging = true;

                if (this.hoverOnRemove) return;
                this.moveMode = true;
            });
        }

        if (this.hide) {
            this.divElement.style.display = 'none';
        }

        if (this.bracketTradePlotline && (this.bracketTradePlotline.profitTargetPlotLine || this.bracketTradePlotline.stopLossPlotLines.length > 0)) { 
            this.bracketTradePlotline?.addBracketIcon();
        }

        if (this.model.id && !this.divElement.containClass('group-click-inited')) {
            this.divElement?.findFirst('.item.left').addEventListener('click', () => {
                this.orderBracketManager?.selectGroup(this.model.id);
            });

            this.divElement?.findFirst('.item.center').addEventListener('click', () => {
                this.orderBracketManager?.selectGroup(this.model.id);
            });

            this.divElement?.findFirst('.badge').addEventListener('click', () => {
                this.orderBracketManager?.selectGroup(this.model.id);
            });

            this.divElement.addClass('group-click-inited');
        }

        setTimeout(() => {
            this.divElement?.ifExistThen((b: HTMLElement) => {
                if (b.containClass('inited') || !this.model.allowMoving) {
                    return;
                }
                b.addClass('inited');
                b.addEventListener('mouseenter', () => {
                    if (this.isPending) {
                        return;
                    }
                    this.mouseHover = true;
                    this.base.tiChartSeries.preventDragging = true;
                    clearTimeout(this.mouseHoverTimer);

                    this.toFront();

                    b.addClass('hover');
                });
                b.addEventListener('click', () => {
                    if (this.isPending) {
                        return;
                    }
                    this.mouseHover = true;
                    this.base.tiChartSeries.preventDragging = true;
                    clearTimeout(this.mouseHoverTimer);

                    this.toFront();

                    b.addClass('hover');
                });
                b.addEventListener('mouseleave', () => {
                    b.removeClass('hover');

                    this.toNormal();

                    this.mouseHoverTimer = setTimeout(() => {
                        this.mouseHover = false;
                        this.base.tiChartSeries.preventDragging = false;
                    }, 500);
                });
                b.findFirst('.right')?.ifExistThen((r: HTMLElement) => {
                    const remove = () => {
                        if (this.disabled || !this.model.allowMoving || this.drawTradeOrdersComponent.removeTradeIds.includes(this.model.id)) return;

                        const shouldDisplayPopup = this.base.tiChartSeries.chartStyleSettings.chartSettingsPopupModel.displayOrderConfirmationPopup;
                        if (shouldDisplayPopup) {
                            const popup = this.base.getComponentsByType(InformationPopupComponent)[0] as InformationPopupComponent;
                            popup?.show(`Cancel Order`, this.model.getCancelOrderMessage(), () => {
                                this.removeTradeOrder();
                            });
                        } else {
                            this.removeTradeOrder();
                        }
                    }
                    if (r.containClass('inited')) {
                        return;
                    }
                    r.classList.add('inited');
                    r.addEventListener('mouseenter', () => {
                        clearTimeout(hoverOnRemoveTimer);
                        r.classList.add('hover');
                        this.hoverOnRemove = true;
                    });
                    r.addEventListener('mouseleave', () => {
                        r.classList.remove('hover');
                        hoverOnRemoveTimer = setTimeout(() => {
                            this.hoverOnRemove = false;
                        }, 300);
                    });
                    r.addEventListener('click', (e: any) => {
                        e.preventDefault();
                        e.stopPropagation();
                        remove();
                    });

                    const s = r.querySelector('svg')
                    if (!s || s.classList.contains('inited')) {
                        return;
                    }
                    s.classList.add('inited');
                    s.addEventListener('click', (e: any) => {
                        e.preventDefault();
                        e.stopPropagation();
                        remove();
                    });
                });
                b.findFirst('.profit-target-button')?.ifExistThen((p: HTMLElement) => {
                    if (p.containClass('inited')) return;
                    p.addClass('inited');
                    if (this.bracketTradePlotline?.requestingProfitTarget) {
                        p.addClass('hidden');
                    }
                    p.addEventListener('mousedown', (e: MouseEvent) => {
                        e.stopPropagation();
                        this.base.tiChartSeries.preventDragging = true;
                    });
                    p.addEventListener('mouseup', () => {
                        setTimeout(() => { this.base.tiChartSeries.preventDragging = false; }, 500);
                    });
                    p.addEventListener('click', (e: any) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (this.disabled || this.orderBracketManager?.ordersAndPositionBracketCount === 0) {
                            this.orderBracketManager.selectedGroupId = this.model.id;
                        }
                        if (!this.bracketTradePlotline?.profitTargetPlotLine && !this.bracketTradePlotline?.requestingProfitTarget) {
                            p.addClass('hidden');
                            this.bracketTradePlotline?.submitProfitTarget();
                        }
                    });
                });
                b.findFirst('.stop-loss-button')?.ifExistThen((p: HTMLElement) => {
                    if (p.containClass('inited')) return;
                    p.addClass('inited');
                    if (this.bracketTradePlotline?.requestingStopLoss) {
                        p.addClass('hidden');
                    }
                    p.addEventListener('mousedown', (e: MouseEvent) => {
                        e.stopPropagation();
                        this.base.tiChartSeries.preventDragging = true;
                    });
                    p.addEventListener('mouseup', () => {
                        setTimeout(() => { this.base.tiChartSeries.preventDragging = false; }, 500);
                    });
                    p.addEventListener('click', (e: any) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (this.disabled || this.orderBracketManager?.ordersAndPositionBracketCount === 0) {
                            this.orderBracketManager.selectedGroupId = this.model.id;
                        }
                        if (!this.bracketTradePlotline?.stopLossPlotLines.length && !this.bracketTradePlotline?.requestingStopLoss) {
                            p.addClass('hidden');
                            this.bracketTradePlotline?.submitStopLoss();
                        }
                    });
                });
            });
        }, 300);
    }

    private removeTradeOrder() {
        this.disable();
        this.drawTradeOrdersComponent.removeTradeIds.push(this.model.id);
        this.base.chartService.requestRemoveChartTradeV2(this.model.id);
        this.bracketTradePlotline?.closeStopLoss();
        this.bracketTradePlotline?.closeProfitTarget();
    }

    private setLeft() {
        const showSmartRisk = this.base.tiChartSeries.chartStyleSettings?.showSmartRiskLevel ?? false;
        const smartRiskLeft = showSmartRisk ? 34 : (this.base.marketingWebChartMode ? 0 : 4);
        this.left = this.base.tiChartSeries.element.clientWidth - this.elementWidth - ((this.EXTRA_RIGHT + smartRiskLeft) * 1.20);
    }

    private setIconPosition() {
        const container = this.base.chartWrapper;
        if (!container) return;

        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) return;

        this.setInitialTop();
        this.setLeft();

        if (isNaN(this.top) || isNaN(this.left)) return;

        this.hide = false;
        if (this.model.price > yAxis.max || this.model.price < yAxis.min) {
            this.hide = true;
        }

        container.querySelectorAll<HTMLElement>(`.trade-order-wrapper.${this.id}`).forEach((el: HTMLElement) => {
            if (this.hide) {
                el.style.display = 'none';
            } else {
                el.style.display = '';
            }
        });

        try {
            this.fObj?.element?.setAttribute('y', this.top);
            this.fObj?.element?.setAttribute('x', this.left);
        } catch { }
    }

    private setInitialTop() {
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) return;

        this.top = yAxis.toPixels(this.model.price, true);

        if (this.base.element.containClass('one')) {
            if (this.base.marketingWebChartMode) {
                this.top += 26;
            } else {
                this.top += 14;
            }
        } else {
            if (this.base.marketingWebChartMode) {
                this.top += 45;
            } else {
                this.top += 33;
            }
        }
    }

    private setElementWidthAndHeight() {
        const element = this.getWrapperElement();
        element.setAttribute('style', 'opacity: 0 !important; visibility: hidden !important; position: absolute !important; width: auto !important');
        this.base.tiChartSeries?.element?.insertAdjacentElement('beforeend', element);
        const width = element.clientWidth;
        const height = element.clientHeight;
        const tradeTypeLower = this.model.tradeType?.toLowerCase();
        const isBracketEntry = tradeTypeLower === 'limit' || tradeTypeLower === 'stop' || tradeTypeLower === 'stoplimit';
        this.elementWidth = Math.ceil(width + (isBracketEntry ? this.EXTRA_WIDTH_FOR_PROFIT_STOP_ICONS : 0));
        this.elementHeight = Math.ceil(height);
        element.remove();
    }


    private getWrapperElement(): HTMLElement {
        const divElement = document.createElement('div');
        divElement.className = `trade-order-wrapper ${this.id}`;
        let title = this.model.getOrderDescriptionTooltip();

        divElement.setAttribute('style', `${this.moveMode ? `cursor: -webkit-grabbing;` : ``}${this.hide ? `display: none;` : ``}${this.model.allowMoving ? `` : `opacity: 0.5 !important; cursor: default !important;`}`);

        let tradeType = this.model.tradeType.toLowerCase();
        if (tradeType) {
            tradeType = tradeType[0].toUpperCase() + tradeType.slice(1);
        }

        const tradeTypeLower = this.model.tradeType?.toLowerCase();
        const isBracketEntry = tradeTypeLower === 'limit' || tradeTypeLower === 'stop' || tradeTypeLower === 'stoplimit';
        const borderStyle = 'dashed';

        const badgeStyle = isBracketEntry
            ? `background: #fff; border: 1px solid ${this.color}; color: ${this.color};`
            : `background: ${this.color};`;
        const leftStyle = isBracketEntry
            ? `border: 1px solid ${this.color}; background-color: #fff; color: ${this.color};`
            : `border: 1px solid ${this.color}; background-color: ${this.color};`;

        const lineWidth = this.base.tiChartSeries.element.clientWidth;

        const hasProfitTarget = this.bracketTradePlotline?.profitTargetPlotLine != null;
        const hasStopLoss = (this.bracketTradePlotline?.stopLossPlotLines?.length ?? 0) > 0;

        divElement.innerHTML = `
            ${isBracketEntry ? `
            <div class="profit-target-button hidden" title="Click to add Profit Target">
                <svg style="cursor:pointer;color:${this.color};">
                    <use href="${this.base.basePath}assetfiles/img/profit-target-v2.svg#profit-target-v2"></use>
                </svg>
            </div>
            <div class="stop-loss-button hidden" title="Click to add Stop Loss">
                <svg style="cursor:pointer;color:${this.color};">
                    <use href="${this.base.basePath}assetfiles/img/stop-loss-v2.svg#stop-loss-v2"></use>
                </svg>
            </div>
            ` : ''}
            <div class="badge" style="${badgeStyle}" title="${title}">${this.model.isLong ? "BUY" : "SELL"}</div>
            <div class="left item" style="${leftStyle}" title="${title}">
                <p class="value">${Math.abs(this.model.shares)}</p>
            </div>
            <div class="center item" style="border: 1px ${borderStyle} ${this.color}; border-left: none; border-right: none;"  title="${title}">
                <p class="value" style="color:${this.color};">${tradeType}</p>
            </div>
            <div class="right item" style="border: 1px ${borderStyle} ${this.color}; border-left: none;${this.model.allowMoving ? `` : `cursor: default !important;`}" title="${title}">
                <svg style="color:${this.color};" class="trade-order" >
                    <use href="${this.base.basePath}assetfiles/img/closev2.svg#closev2"></use>
                </svg>
            </div>
            <span class="line" style="position: absolute; height: 1px; top: 10px; width: ${lineWidth}px; background-image: repeating-linear-gradient(to right, ${this.color} 0px, ${this.color} 3px, transparent 3px, transparent 6px);"></span>
        `;

        return divElement;
    }
}